import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, inject, Injector, signal } from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxHttpErrorResponse } from '@onyx/angular';
import {
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxDropdownComponent,
  OnyxFormMode,
  OnyxLanguagePipe,
  OnyxModalComponent,
  OnyxToastService,
} from '@onyx/angular';
import { map, Subject, switchMap } from 'rxjs';
import { DictionaryCode } from '../../../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../../../common/helpers/validation.helper';
import { DictionariesService } from '../../../../../../common/services/dictionaries.service';
import { DictionariesNamesTranslationFormComponent } from '../../../common/components/forms/dictionaries-names-translation-form/dictionaries-names-translation-form.component';
import { CompanyDictionaryCode } from '../../../common/enums/company-dictionary-code';
import { DictionariesHelper } from '../../../common/helpers/dictionaries.helper';
import type { CompanyGoodType } from '../../../common/interfaces/company-good-type';
import { CompanyDictionariesService } from '../../../common/services/company-dictionaries.service';

export type DictionariesGoodTypeForm = ReturnType<
  ReturnType<DictionariesGoodTypeModalComponent['buildForm']>['getRawValue']
>;

export type DictionariesGoodTypeModalData =
  | {
      mode: OnyxFormMode.ADD;
      name?: string;
    }
  | {
      mode: OnyxFormMode.EDIT;
      goodType: CompanyGoodType;
    };

@Component({
  selector: 'app-dictionaries-good-type-modal',
  imports: [
    OnyxModalComponent,
    DictionariesNamesTranslationFormComponent,
    OnyxDropdownComponent,
    OnyxButtonComponent,
    AsyncPipe,
    ReactiveFormsModule,
    TranslatePipe,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './dictionaries-good-type-modal.component.html',
  styleUrl: './dictionaries-good-type-modal.component.scss',
})
export class DictionariesGoodTypeModalComponent implements OnInit {
  protected readonly data = inject<DictionariesGoodTypeModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastService = inject(OnyxToastService);
  private readonly injector = inject(Injector);
  private readonly companyDictionariesService = inject(
    CompanyDictionariesService,
  );
  private readonly languagePipe = inject(OnyxLanguagePipe);
  private readonly dictionariesHelper = inject(DictionariesHelper);

  protected readonly I18N = 'dictionaries';
  protected readonly OnyxFormMode = OnyxFormMode;

  protected readonly companyUnits$ = this.companyDictionariesService
    .listDictionary(CompanyDictionaryCode.UNIT)
    .pipe(
      map((units) =>
        units.map((unit) => ({
          name: this.languagePipe.transform(unit.names),
          value: unit.uuid,
        })),
      ),
    );
  protected readonly adrClasses$ = this.dictionariesService.getDictionary(
    DictionaryCode.ADR_CLASS,
  );

  protected form = this.buildForm();
  protected loading = signal(false);
  protected close$ = new Subject<void>();

  public ngOnInit(): void {
    if (this.data.mode === OnyxFormMode.EDIT) {
      this.form.patchValue({
        ...this.data.goodType,
        relatedUnits: this.data.goodType.relatedUnits.map((unit) => unit.uuid),
      });
    }
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const form = this.form.getRawValue();

    let action$;
    switch (this.data.mode) {
      case OnyxFormMode.ADD:
        action$ = this.companyDictionariesService.addDictionaryItem(
          CompanyDictionaryCode.GOOD_TYPE,
          form,
        );
        break;

      case OnyxFormMode.EDIT:
        {
          const { goodType } = this.data;
          action$ = this.dictionariesHelper
            .confirmEdit(CompanyDictionaryCode.GOOD_TYPE, goodType, this.close$)
            .pipe(
              switchMap(() =>
                this.companyDictionariesService.editDictionaryItem(
                  goodType.uuid,
                  form,
                ),
              ),
            );
        }
        break;
    }

    this.loading.set(true);
    action$
      .subscribe({
        next: () => {
          this.toastService.showSuccess({
            message:
              this.data.mode === OnyxFormMode.ADD
                ? `${this.I18N}.dictionaryAdded`
                : `${this.I18N}.dictionaryEdited`,
          });
          this.close$.next();
        },
        error: (response: OnyxHttpErrorResponse) =>
          this.dictionariesHelper.handleEditError(
            response,
            CompanyDictionaryCode.GOOD_TYPE,
            form,
          ),
      })
      .add(() => this.loading.set(false));
  }

  private buildForm() {
    return this.fb.group({
      names: DictionariesNamesTranslationFormComponent.buildFom(this.fb),
      relatedUnits: this.fb.control<string[] | null>(null, [
        Validators.required,
      ]),
      adrClass: this.fb.control<string | null>(null),
    });
  }
}
